import { getControlSchema } from './controlSchema';

const FIELD_LABELS = {
  memberId: '用戶 ID',
  direction: '控獎方向',
  enabled: '狀態',
  minValue: '隨機最小值',
  maxValue: '隨機最大值',
  isRealOdds: '賠率配置',
  minRandomOdds: '隨機賠率最小值',
  maxRandomOdds: '隨機賠率最大值',
  startKillAmount: '系統控起控金額',
  killProbability: '系統控概率',
};

export function fieldLabel(fieldKey) {
  return FIELD_LABELS[fieldKey] || fieldKey;
}

function supportsPersonalMinMax(playType) {
  return playType === 'PERPETUAL' || playType === 'TIME_LIMITED';
}

export function personalSupportsMinMax(playType) {
  return supportsPersonalMinMax(playType);
}

function emptyPersonalForm(playType) {
  const schema = getControlSchema(playType, 'personal');
  const form = {
    memberId: '',
    enabled: true,
  };
  if (schema?.directionField === 'controlWin') {
    form.direction = 'false';
  } else {
    form.direction = 'LOSE';
  }
  if (supportsPersonalMinMax(playType)) {
    form.minValue = '';
    form.maxValue = '';
  }
  return form;
}

function emptyGlobalForm(playType) {
  const schema = getControlSchema(playType, 'global');
  const form = {};
  (schema?.formFields || []).forEach((key) => {
    if (key === 'enabled') {
      form.enabled = playType === 'FUTURES';
    } else if (key === 'direction') {
      if (playType === 'PERPETUAL') {
        form.direction = 'LOSE';
      } else if (schema.directionField === 'controlWin') {
        form.direction = 'false';
      } else {
        form.direction = 'LOSE';
      }
    } else if (key === 'isRealOdds') {
      form.isRealOdds = 'false';
    } else {
      form[key] = '';
    }
  });
  return form;
}

function readDirectionValue(record, schema) {
  if (!schema) {
    return '';
  }
  if (schema.directionField === 'controlWin') {
    if (record?.controlWin === true) {
      return 'true';
    }
    if (record?.controlWin === false) {
      return 'false';
    }
    return '';
  }
  return record?.direction || '';
}

export function personalToForm(record, playType) {
  if (!record) {
    return emptyPersonalForm(playType);
  }
  const schema = getControlSchema(playType, 'personal');
  const form = {
    memberId: record.memberId != null ? String(record.memberId) : '',
    enabled: Boolean(record.enabled),
    direction: readDirectionValue(record, schema),
  };
  if (supportsPersonalMinMax(playType)) {
    form.minValue = record.minValue != null ? String(record.minValue) : '';
    form.maxValue = record.maxValue != null ? String(record.maxValue) : '';
  }
  return form;
}

export function globalToForm(record, playType) {
  if (!record) {
    return emptyGlobalForm(playType);
  }
  const schema = getControlSchema(playType, 'global');
  const form = {};
  (schema?.formFields || []).forEach((key) => {
    if (key === 'enabled') {
      form.enabled = Boolean(record.enabled);
    } else if (key === 'direction') {
      form.direction = readDirectionValue(record, schema);
    } else if (key === 'isRealOdds') {
      form.isRealOdds = record.isRealOdds === false ? 'false' : 'true';
    } else if (record[key] != null) {
      form[key] = String(record[key]);
    } else {
      form[key] = '';
    }
  });
  return form;
}

function parseOptionalNumber(value) {
  if (value == null || value === '') {
    return null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function applyDirectionToPayload(payload, form, schema) {
  if (!schema?.directionField) {
    return;
  }
  if (schema.directionField === 'controlWin') {
    payload.controlWin = form.direction === 'true';
    return;
  }
  payload.direction = form.direction || null;
}

export function buildPersonalSavePayload(form, playType, record) {
  const schema = getControlSchema(playType, 'personal');
  const memberId = Number(form.memberId);
  const payload = {
    playType,
    memberId,
    enabled: Boolean(form.enabled),
  };
  if (record?.id) {
    payload.id = record.id;
  }
  applyDirectionToPayload(payload, form, schema);
  if (supportsPersonalMinMax(playType)) {
    payload.minValue = parseOptionalNumber(form.minValue);
    payload.maxValue = parseOptionalNumber(form.maxValue);
  }
  return payload;
}

export function buildGlobalSavePayload(form, playType, record) {
  const schema = getControlSchema(playType, 'global');
  const payload = {
    playType,
    productId: null,
  };
  if (record?.id) {
    payload.id = record.id;
  }

  (schema?.formFields || []).forEach((key) => {
    if (key === 'enabled') {
      payload.enabled = Boolean(form.enabled);
      payload.controlStatus = form.enabled ? 1 : 0;
    } else if (key === 'direction') {
      applyDirectionToPayload(payload, form, schema);
    } else if (key === 'isRealOdds') {
      payload.isRealOdds = form.isRealOdds === 'true';
    } else {
      payload[key] = parseOptionalNumber(form[key]);
    }
  });

  if (playType !== 'FUTURES' && payload.enabled) {
    payload.controlStatus = 1;
  }
  if (playType === 'FUTURES' && payload.enabled != null) {
    payload.controlStatus = payload.enabled ? 1 : 0;
  }

  return payload;
}

export function validatePersonalForm(form, playType) {
  const memberId = Number(form.memberId);
  if (!Number.isFinite(memberId) || memberId <= 0) {
    return '請輸入有效的用戶 ID';
  }
  if (!form.direction) {
    return '請選擇控獎方向';
  }
  if (supportsPersonalMinMax(playType)) {
    const min = parseOptionalNumber(form.minValue);
    const max = parseOptionalNumber(form.maxValue);
    if (min != null && max != null && min > max) {
      return playType === 'TIME_LIMITED'
        ? '隨機最小值不可大於最大值'
        : '滑價最小值不可大於最大值';
    }
  }
  return '';
}

export function validateGlobalForm(form, playType) {
  if (playType === 'FUTURES') {
    if (form.isRealOdds === 'false') {
      const min = parseOptionalNumber(form.minRandomOdds);
      const max = parseOptionalNumber(form.maxRandomOdds);
      if (min != null && max != null && min > max) {
        return '隨機賠率最小值不可大於最大值';
      }
    }
    const probability = parseOptionalNumber(form.killProbability);
    if (probability != null && (probability < 0 || probability > 100)) {
      return '系統控概率須介於 0～100';
    }
    return '';
  }

  if (form.enabled) {
    if (!form.direction) {
      return '請選擇控獎方向';
    }
    const min = parseOptionalNumber(form.minValue);
    const max = parseOptionalNumber(form.maxValue);
    if (min != null && max != null && min > max) {
      return '隨機最小值不可大於最大值';
    }
  }
  return '';
}

export function buildPersonalPageParams(pageNo, playType, filters) {
  const params = {
    pageNo,
    pageSize: 20,
    playType,
  };
  const memberId = filters.memberId?.trim();
  if (memberId) {
    params.memberId = memberId;
  }
  if (filters.enabled === 'true') {
    params.enabled = true;
  } else if (filters.enabled === 'false') {
    params.enabled = false;
  }
  return params;
}
